import Image from "next/image";
import { CardAnimated } from "@/components/ui/card";
import { CornerFlag } from "@/components/ui/corner-flag";
import { cn } from "@/lib/utils";

type CardPhotoProps = {
  photo: string;
  photoDark?: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
  tags?: string[];
  gradientBorder?: boolean;
};

export function CardPhoto({ photo, photoDark, alt, className, onClick, tags = [], gradientBorder = false }: CardPhotoProps) {
  const isUnicorn = tags.includes('Unicorn');
  const isExit = tags.includes('Exit');
  const gradient = gradientBorder
    ? (isUnicorn && isExit)
      ? 'linear-gradient(135deg, #A855F7, #EC4899 40%, #10B981 60%, #059669)'
      : isUnicorn
        ? 'linear-gradient(to right, #A855F7, #EC4899)'
        : isExit
          ? 'linear-gradient(to right, #10B981, #059669)'
          : null
    : null;

  if (gradient) {
    return (
      <div
        className={cn("rounded-xl p-[1px] w-full h-full aspect-square", className)}
        style={{ background: gradient }}
        onClick={onClick}
      >
        <CardAnimated
          className="relative flex items-center justify-center px-3 md:px-5 py-3 w-full h-full rounded-[11px]"
        >
          <CornerFlag tags={tags} />
          <Image
            src={photo}
            alt={alt || "Image"}
            width={0}
            height={0}
            sizes="(max-width: 640px) 120px, (max-width: 768px) 140px, 160px"
            style={{ width: '100%', height: 'auto' }}
            className={`w-full h-full object-contain max-h-14 ${
              photoDark ? 'dark:hidden' : 'dark:invert dark:grayscale dark:brightness-0'
            }`}
          />
          {photoDark && (
            <Image
              src={photoDark}
              alt={alt || "Image"}
              width={0}
              height={0}
              sizes="(max-width: 640px) 120px, (max-width: 768px) 140px, 160px"
              style={{ width: '100%', height: 'auto' }}
              className="w-full h-full object-contain max-h-14 hidden dark:block dark:invert dark:grayscale dark:brightness-0"
            />
          )}
        </CardAnimated>
      </div>
    );
  }

  return (
    <CardAnimated
      className={cn("relative flex items-center justify-center px-3 md:px-5 py-3 w-full h-full aspect-square", className)}
      onClick={onClick}
    >
      {/* Corner flag for New/Updated indicators */}
      <CornerFlag tags={tags} />

      {/* Light mode image or if no dark image is provided */}
      <Image
        src={photo}
        alt={alt || "Image"}
        width={0}
        height={0}
        sizes="(max-width: 640px) 120px, (max-width: 768px) 140px, 160px"
        style={{ width: '100%', height: 'auto' }}
        className={`w-full h-full object-contain max-h-14 ${
          photoDark ? 'dark:hidden' : 'dark:invert dark:grayscale dark:brightness-0'
        }`}
      />

      {/* Dark mode image - shown only in dark mode if available */}
      {photoDark && (
        <Image
          src={photoDark}
          alt={alt || "Image"}
          width={0}
          height={0}
          sizes="(max-width: 640px) 120px, (max-width: 768px) 140px, 160px"
          style={{ width: '100%', height: 'auto' }}
          className="w-full h-full object-contain max-h-14 hidden dark:block dark:invert dark:grayscale dark:brightness-0"
        />
      )}
    </CardAnimated>
  );
}